import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { fetchTax } from '../../../actions'
import { setTaxLoading } from '../checkoutFunctions'
import { createTaxFormData, shouldCalculateTax } from '../services/taxCalculationService'

/**
 * Custom hook to handle tax calculation
 * @param {object} params - Tax calculation parameters
 * @param {boolean} params.isInitializing - Whether checkout is initializing
 * @param {boolean} params.isCartLoaded - Whether cart is loaded
 * @param {Array} params.cartItems - Array of cart items
 * @param {object} params.checkout - Checkout state object
 * @param {object} params.selectedRatesByGroup - Selected rates by group (for ShipEngine)
 * @param {object} params.portal - Portal configuration
 * @param {object} params.currentUser - Current user object
 * @param {object} params.managedCustomerAddresses - Managed customer addresses
 * @returns {object} - { calculateTax: function }
 */
export const useTaxCalculation = ({
  isInitializing,
  isCartLoaded,
  cartItems,
  checkout,
  selectedRatesByGroup,
  portal,
  currentUser,
  managedCustomerAddresses
}) => {
  const dispatch = useDispatch()

  // Extract primitive values and specific properties to prevent unnecessary re-renders
  const {
    selectedCustomerAddress,
    taxReduction,
    checkoutData,
    appliedPromotions,
    subtotalReduction,
    shippingReduction,
    shippingReductionEncrypted,
    productReduction,
    multiAddressSelection
  } = checkout || {}

  const checkoutShipping = checkoutData?.shipping
  const { multiple_address_shipping: multipleAddressShipping } = portal || {}
  const { id: currentUserId } = currentUser || {}
  const { epcid: managedCustomerEpcid } = managedCustomerAddresses || {}

  // Create stable serialized keys for complex objects/arrays
  const selectedAddressKey = useMemo(() => JSON.stringify(selectedCustomerAddress), [selectedCustomerAddress])
  const multiAddressSelectionKey = useMemo(() => JSON.stringify(multiAddressSelection), [multiAddressSelection])
  const appliedPromotionsKey = useMemo(() => JSON.stringify(appliedPromotions), [appliedPromotions])
  const cartItemsKey = useMemo(() => JSON.stringify(cartItems), [cartItems])

  const calculateTax = useCallback(() => {
    const shouldCalculate = shouldCalculateTax({
      isInitializing,
      isCartLoaded,
      cartItems,
      checkout,
      portal,
      currentUser
    })

    if (!shouldCalculate) return

    dispatch(setTaxLoading(true))

    const formData = createTaxFormData({
      checkout,
      checkoutRates: { selectedRatesByGroup },
      cartItems,
      currentUser,
      portal,
      managedCustomerAddresses
    })

    dispatch(fetchTax(formData)).finally(() => {
      dispatch(setTaxLoading(false))
    })
  }, [
    isInitializing,
    isCartLoaded,
    selectedAddressKey,
    taxReduction,
    checkoutShipping,
    appliedPromotionsKey,
    subtotalReduction,
    shippingReduction,
    shippingReductionEncrypted,
    productReduction,
    multiAddressSelectionKey,
    multipleAddressShipping,
    currentUserId,
    managedCustomerEpcid,
    cartItemsKey,
    selectedRatesByGroup,
    currentUser,
    portal,
    managedCustomerAddresses,
    dispatch
  ])

  return { calculateTax }
}
